import {
  AbstractProvider,
  AbstractSigner,
  solidityPackedKeccak256,
} from 'ethers'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTimeoutFn } from 'react-use'
import { Groth16Proof, PublicSignals } from 'snarkjs'
import { Chain } from 'viem'
import { useWatchAsset } from 'wagmi'

import { config, useWeb3Context } from '@/contexts/Web3Provider'
import { createContract } from '@/contexts/Web3Provider/helpers/contracts'
import { getSignerOrProvider } from '@/contexts/Web3Provider/helpers/providers'
import { LivenessToken__factory } from '@/contexts/Web3Provider/types'
import { Groth16VerifierHelper } from '@/contexts/Web3Provider/types/LivenessToken'
import { ErrorHandler } from '@/helpers'
import { CachedRemoteFileLoader } from '@/helpers/chunked-loader'
import { useLoading } from '@/hooks/loading'

export type ZkProof = {
  proof: Groth16Proof
  publicSignals: PublicSignals
}

function createLivenessTokenContract(
  address: string,
  provider: AbstractProvider | AbstractSigner,
) {
  const { contractInstance, contractInterface } = createContract(
    address,
    provider,
    LivenessToken__factory,
  )

  return {
    contractInstance,
    contractInterface,

    loadDetails: async () => {
      const [decimals, name, symbol, totalSupply, threshold] =
        await Promise.all([
          contractInstance.decimals(),
          contractInstance.name(),
          contractInstance.symbol(),
          contractInstance.totalSupply(),
          contractInstance.getThreshold(),
        ])

      return {
        decimals,
        name,
        symbol,
        totalSupply,
        threshold,
      }
    },
  }
}

export const BASE_THRESHOLD = 0.4
export const CHAIN_ID = 11155111
const CONTRACT_ADDR = '0x0aA43eDC488E6800532a183E8b029F66b7Dc1B0c'

type LivenessTokenDetails = {
  decimals: bigint
  name: string
  symbol: string
  totalSupply: bigint
  balance: bigint
  threshold: bigint
}

type HomepageContext = {
  livenessTokenDetails: LivenessTokenDetails | undefined
  nonce: bigint
  isValidChain: boolean
  isLoading: boolean
  targetChain: Chain
  circuitsLoadingProgress: number
  currDecorDesc: string
  setCurrDecorDesc: (v: string) => void

  addTokenToWallet: () => Promise<void>
  claimAirdrop: (zkProof: ZkProof) => Promise<void>
  reloadLivenessTokenDetails: () => Promise<void>
  getCircuits: () => Promise<[Buffer<ArrayBufferLike>, Buffer<ArrayBufferLike>]>
}

const homepageContext = createContext<HomepageContext>({
  livenessTokenDetails: {} as LivenessTokenDetails,
  nonce: 0n,
  isValidChain: false,
  isLoading: false,
  targetChain: config.chains[0],
  circuitsLoadingProgress: 0,
  currDecorDesc: '',
  setCurrDecorDesc: () => {},

  addTokenToWallet: () => Promise.resolve(),
  claimAirdrop: () => Promise.resolve(),
  reloadLivenessTokenDetails: () => Promise.resolve(),
  getCircuits: async () => {
    return [
      new Uint8Array() as Buffer<ArrayBufferLike>,
      new Uint8Array() as Buffer<ArrayBufferLike>,
    ]
  },
})

export const HomepageContextProvider = ({ children }: PropsWithChildren) => {
  const { client, address, chain } = useWeb3Context()

  const { watchAsset } = useWatchAsset()

  const isValidChain = useMemo(() => {
    return chain?.id === CHAIN_ID
  }, [chain?.id])

  const nonce = useMemo(() => {
    const value = solidityPackedKeccak256(
      ['address', 'uint256'],
      [CONTRACT_ADDR, CHAIN_ID],
    )

    const nonce =
      BigInt(value) &
      0x00ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffn

    return nonce
  }, [])

  const livenessTokenContract = useMemo(() => {
    if (!client) return undefined

    const jsonRpcSignerOrProvider = getSignerOrProvider(client)

    return createLivenessTokenContract(CONTRACT_ADDR, jsonRpcSignerOrProvider)
  }, [client])

  const targetChain = useMemo(() => {
    return config.chains.find(el => el.id === CHAIN_ID) ?? config.chains[0]
  }, [])

  const [currDecorDesc, setCurrDecorDesc] = useState('')
  const [circuitsLoadingProgress, setProgress] = useState(0)
  const [, setError] = useState<Error>()
  const [zkeyFileBytes, setZkeyFileBytes] = useState<Buffer<ArrayBufferLike>>()
  const [wtnsCalcFileBytes, setWtnsCalcFileBytes] =
    useState<Buffer<ArrayBufferLike>>()
  const filesLoaderPromiseResolverRef = useRef<
    null | ((value: [Buffer<ArrayBufferLike>, Buffer<ArrayBufferLike>]) => void)
  >(null)

  const zKeyLoader = useMemo(
    () =>
      new CachedRemoteFileLoader(
        'https://tmp-dl.fra1.digitaloceanspaces.com/ZkLivenessAirdrop/circuit_final.zkey',
        {
          onProgress: progress => setProgress(progress),
          onError: error => setError(error),
        },
      ),
    [],
  )

  const wtnsWasmLoader = useMemo(
    () =>
      new CachedRemoteFileLoader(
        'https://tmp-dl.fra1.digitaloceanspaces.com/ZkLivenessAirdrop/zkLivenessFieldBits.wasm',
        {},
      ),
    [],
  )

  const loadFiles = useCallback(async () => {
    setProgress(0)

    if (zkeyFileBytes?.length && wtnsCalcFileBytes?.length) {
      return [zkeyFileBytes, wtnsCalcFileBytes]
    }

    const [zkeyFileBytesResponse, wtnsCalcFileBytesResponse] =
      await Promise.all([zKeyLoader.loadFile(), wtnsWasmLoader.loadFile()])

    setZkeyFileBytes(zkeyFileBytesResponse)
    setWtnsCalcFileBytes(wtnsCalcFileBytesResponse)

    return [zkeyFileBytesResponse, wtnsCalcFileBytesResponse]
  }, [wtnsCalcFileBytes, wtnsWasmLoader, zKeyLoader, zkeyFileBytes])

  const getCircuits = useCallback(async (): Promise<
    [Buffer<ArrayBufferLike>, Buffer<ArrayBufferLike>]
  > => {
    if (
      circuitsLoadingProgress >= 100 &&
      zkeyFileBytes?.length &&
      wtnsCalcFileBytes?.length
    ) {
      return [zkeyFileBytes, wtnsCalcFileBytes]
    }

    return new Promise(resolve => {
      filesLoaderPromiseResolverRef.current = resolve
    })
  }, [circuitsLoadingProgress, zkeyFileBytes, wtnsCalcFileBytes])

  useTimeoutFn(loadFiles, 500)

  useEffect(() => {
    if (
      circuitsLoadingProgress < 100 ||
      !filesLoaderPromiseResolverRef.current ||
      !zkeyFileBytes?.length ||
      !wtnsCalcFileBytes?.length
    ) {
      return
    }

    filesLoaderPromiseResolverRef.current([zkeyFileBytes, wtnsCalcFileBytes])
    filesLoaderPromiseResolverRef.current = null
  }, [circuitsLoadingProgress, zkeyFileBytes, wtnsCalcFileBytes])

  const {
    data: livenessTokenDetails,
    isLoading,
    update: reloadLivenessTokenDetails,
  } = useLoading(
    undefined,
    async () => {
      if (!livenessTokenContract || !isValidChain) return

      const { decimals, name, symbol, totalSupply, threshold } =
        await livenessTokenContract.loadDetails()

      const balance = address
        ? await livenessTokenContract.contractInstance.balanceOf(address)
        : 0n

      return {
        decimals,
        name,
        symbol,
        totalSupply,
        balance,
        threshold,
      }
    },
    {
      loadArgs: [livenessTokenContract, address, isValidChain],
    },
  )

  const addTokenToWallet = useCallback(async () => {
    if (!livenessTokenContract || !livenessTokenDetails) return

    try {
      watchAsset({
        type: 'ERC20',
        options: {
          address: CONTRACT_ADDR,
          symbol: livenessTokenDetails.symbol,
          decimals: +livenessTokenDetails.decimals.toString(),
        },
      })
    } catch (error) {
      ErrorHandler.process(error)
    }
  }, [livenessTokenDetails, livenessTokenContract, watchAsset])

  const claimAirdrop = useCallback(
    async (zkProof: ZkProof) => {
      if (!livenessTokenDetails || !livenessTokenContract || !address || !chain)
        return

      const proofStruct: Groth16VerifierHelper.ProofPointsStruct = {
        a: [zkProof.proof.pi_a[0], zkProof.proof.pi_a[1]],
        b: [
          [zkProof.proof.pi_b[0][1], zkProof.proof.pi_b[0][0]],
          [zkProof.proof.pi_b[1][1], zkProof.proof.pi_b[1][0]],
        ],
        c: [zkProof.proof.pi_c[0], zkProof.proof.pi_c[1]],
      }

      const tx = await livenessTokenContract.contractInstance.claim(
        address,
        proofStruct,
      )

      await tx.wait()
    },
    [address, chain, livenessTokenDetails, livenessTokenContract],
  )

  return (
    <homepageContext.Provider
      value={{
        livenessTokenDetails: livenessTokenDetails,
        nonce,
        isValidChain,
        isLoading,
        targetChain,
        circuitsLoadingProgress,
        currDecorDesc,
        setCurrDecorDesc,

        addTokenToWallet,
        claimAirdrop,
        reloadLivenessTokenDetails,
        getCircuits,
      }}
    >
      {children}
    </homepageContext.Provider>
  )
}

export const useHomepageContext = () => useContext(homepageContext)
